// Detecteert ?share= bij opstart en toont de importmodal (AGENTS.md §11).
import { useEffect, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import { sanitizeActivityList } from '../../domain/activities';
import { decodeSharePayload, ShareError } from '../../domain/share';
import type { Activity, ImportMode } from '../../domain/types';
import { useActivities, useToast } from '../../state/AppProviders';
import { sharePreview, SAVE_FAILED_TEXT } from '../menu/dataTransfer';
import { btnPrimary, btnTonal } from '../menu/ui';
import { removeShareParam } from './shareUrl';

export default function ShareImportGate() {
  const { importActivities } = useActivities();
  const { showToast } = useToast();
  const [raw, setRaw] = useState<unknown[] | null>(null);
  const [busy, setBusy] = useState(false);
  const started = useRef(false);
  const mergeRef = useRef<HTMLButtonElement>(null);
  const toastRef = useRef(showToast);
  toastRef.current = showToast;

  useEffect(() => {
    // StrictMode mount-unmount-mount: de parameter is na de eerste run al weg, dus maar één keer lezen.
    if (started.current) return;
    started.current = true;
    const { value, cleanedUrl } = removeShareParam(window.location.href);
    if (value === null) return;
    window.history.replaceState(window.history.state, '', cleanedUrl);
    decodeSharePayload(value).then(
      (items) => {
        if (sanitizeActivityList(items).length === 0) {
          toastRef.current('Deze deellink bevat geen geldige activiteiten.');
          return;
        }
        setRaw(items);
      },
      (err) => toastRef.current(err instanceof ShareError ? err.message : 'Deze deellink kon niet worden geopend.'),
    );
  }, []);

  if (!raw) return null;
  const list: Activity[] = sanitizeActivityList(raw);
  const { lines, more } = sharePreview(list);

  const run = async (mode: ImportMode) => {
    if (busy) return;
    setBusy(true);
    try {
      const n = await importActivities(raw, mode);
      toastRef.current(
        mode === 'merge' ? `${n} activiteiten samengevoegd met je lijst.` : `${n} activiteiten ingeladen. De vorige lijst is vervangen.`,
      );
      setRaw(null);
    } catch {
      toastRef.current(SAVE_FAILED_TEXT);
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Gedeelde activiteiten"
      onClose={() => setRaw(null)}
      initialFocusRef={mergeRef}
      description={`Iemand deelde ${list.length} activiteiten met je.`}
    >
      <ul className="mb-5 list-disc space-y-1 pl-5 text-sm">
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
        {more > 0 && <li className="list-none text-slate-600 dark:text-slate-400">en {more} meer</li>}
      </ul>
      <div className="flex flex-col gap-2">
        <button ref={mergeRef} type="button" disabled={busy} autoFocus onClick={() => void run('merge')} className={btnPrimary}>
          Samenvoegen met huidige gegevens
        </button>
        <button type="button" disabled={busy} onClick={() => void run('overwrite')} className={btnTonal}>
          Bestaande gegevens overschrijven
        </button>
        <button type="button" onClick={() => setRaw(null)} className={btnTonal}>
          Annuleren
        </button>
      </div>
    </Modal>
  );
}
